<template>
  <div>
    <BaseTitle :title="$t(edit ? 'content.group_edit' : 'content.group_create')" />

    <!-- Class Information -->
    <v-card class="pa-8 mb-6">
      <h3 class="app-section-title">{{ $t('content.group_name') }}</h3>
      <v-row>
        <v-col cols="12" md="6" class="py-0">
          <form-group :name="$t('content.class_level')" :validator="$v.form.gradeId">
            <template slot-scope="{ attrs }">
              <v-select
                v-model="form.gradeId"
                v-bind="attrs"
                :disabled="form.assignedToActiveClassroom == true"
                item-value="gradeId"
                item-text="name"
                :items="class_level"
                :label="$t('content.class_level')"
              />
            </template>
          </form-group>
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <form-group :name="$t('form.status')" :validator="$v.form.status">
            <template slot-scope="{ attrs }">
              <v-select
                :items="status"
                :label="$t('form.status')"
                :disabled="form.assignedToActiveClassroom == true"
                v-bind="attrs"
                v-model="form.status"
                width="100"
              >
                <template #item="{item}">
                  {{ $t(item.text) }}
                </template>
                <template #selection="{item}">
                  {{ $t(item.text) }}
                </template>
              </v-select>
            </template>
          </form-group>
        </v-col>
      </v-row>
    </v-card>

    <!-- Course List -->
    <v-card class="pa-8 mb-6">
      <div class="d-flex align-center justify-space-between">
        <h3 class="app-section-title mb-6">{{ $t('content.student_list') }}</h3>
        <div class="d-flex justify-center align-center">
          <div>{{ $t('paginate.show') }}</div>
          <v-select :items="optionList" v-model="itemsPerPage" required class="mx-2" style="width: 70px" />
          <div>{{ $t('paginate.entries') }}</div>
        </div>
        <div>
          <v-text-field v-model="search" :label="$t('search')">
            <template #prepend-inner>
              <svg-icon icon="ic-search" class="primary--text mr-3" />
            </template>
          </v-text-field>
        </div>
      </div>
      <v-data-table
        ref="datatable"
        v-model="form.students"
        item-key="studentId"
        :headers="tableHeader"
        :loading="table.loading"
        :items="students"
        show-select
        hide-default-footer
        class="table-custom"
        @page-count="pageCount = $event"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :search="search"
      >
        <template #header.data-table-select> <v-simple-checkbox v-model="selectAll" :disabled="edit && !form.status" @click="init" /> </template>
        <template #item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            :disabled="item.assignedToActiveClassroomCourse || (edit && !form.status)"
            :value="isSelected"
            @input="select($event)"
            @click="onClickCheck(item, students.indexOf(item))"
          />
        </template>
        <template #no-data><NoData /></template>
      </v-data-table>
      <div class="app-paginate">
        <div class="app-paginate-info">
          {{ $t('paginate.showing') }} {{ min }} {{ $t('paginate.to') }} {{ max }} {{ $t('paginate.of') }} {{ students.length }}
          {{ $t('paginate.entries') }}
        </div>
        <v-pagination v-model="page" :length="pageCount"></v-pagination>
      </div>
    </v-card>

    <!-- Dialog Student -->
    <v-dialog v-model="dialogPopUp" persistent max-width="607">
      <v-card>
        <div class="d-flex justify-center"><img src="@/assets/images/ic-alert.png" class="mt-5 mb-5" /></div>
        <v-card-title class="headline d-flex justify-center">{{ $t('content.group_popup4') }}</v-card-title>
        <v-card-text>
          <b>{{ temporaryStudent.fullName }}</b>
          {{ $t('content.group_popup1') }}
          <b>{{ temporaryStudent.groupName }}</b>
          {{ $t('content.group_popup2') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="popUpDisagree">{{ $t('disagree') }}</v-btn>
          <v-btn text @click="popUpAgree">{{ $t('aggree') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Button Controls -->
    <div class="d-flex justify-end align-center pa-8">
      <v-btn color="primary" class="btn-primary-custom mr-3" outlined @click="backToList">{{ $t('cancel') }}</v-btn>
      <v-btn color="primary" class="btn-primary-custom" type="submit" :loading="loading" @click="submit()">{{ $t(edit ? 'save' : 'submit') }}</v-btn>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import BaseTitle from '@/components/base/Title';
import NoData from '@/components/table/NoData';
import ripple from 'vuetify/lib/directives/ripple';

export default {
  name: 'GroupCreateEdit',
  directives: {
    ripple
  },
  components: {
    BaseTitle,
    NoData
  },
  computed: {
    ...mapGetters({
      status: 'Reference/itemStatus',
      user: 'Auth/user',
      optionList: 'table/optionList'
    }),
    tableHeader() {
      return [
        { text: this.$t('table.name'), value: 'fullName' },
        { text: 'Last Group', value: 'groupName' },
        { text: 'NIS', value: 'identityNumber' }
      ];
    },
    selectAll: {
      get() {
        if (this.students.filter(s => s.assignedToOtherGroup).length === this.students.length) {
          return false;
        } else {
          return this.form.students.filter(s => !s.assignedToOtherGroup).length == this.students.filter(s => !s.assignedToOtherGroup).length;
        }
      },
      set(checked) {
        if (checked) {
          this.form.students = this.form.students.filter(items => items.assignedToActiveClassroomCourse);
          this.students.forEach(student => {
            if (!student.assignedToOtherGroup) {
              this.form.students.push(student);
            }
          });
        } else {
          this.form.students = this.form.students.filter(items => items.assignedToActiveClassroomCourse);
          this.students.map(student => {
            student.assignedToOtherGroup = !student.assigned;
            return student;
          });
        }
      }
    }
  },
  watch: {
    user: {
      deep: true,
      immediate: true,
      handler: function(value) {
        this.form.schoolId = value.schoolId;
      }
    },
    page: {
      immediate: true,
      handler(val) {
        this.max = val * this.itemsPerPage <= this.students.length ? val * this.itemsPerPage : this.students.length;
        this.min = val * this.itemsPerPage <= this.students.length ? this.max - this.itemsPerPage + 1 : (this.page - 1) * this.itemsPerPage + 1;
      }
    }
  },
  validations: {
    form: {
      gradeId: { required },
      status: { required }
    }
  },
  data: () => ({
    dialogPopUp: false,
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    class_level: [],
    search: '',
    dialogCourse: false,
    dialogCourseEdit: false,
    edit: false,
    loading: false,
    temporaryStudent: {},
    students: [],
    form: {
      gradeId: '',
      status: '',
      assignedToActiveClassroom: '',
      students: []
    },
    table: {
      loading: false
    },
    min: 0,
    max: 0,
    key: 0
  }),
  methods: {
    init() {
      if (this.students.filter(s => s.assignedToOtherGroup).length === this.students.length) {
        this.$store.dispatch('general/showErrorNotification', { message: 'Must Selected Manual' });
      }
    },
    onClickCheck(item, key) {
      if (this.edit && !this.form.status) return;

      this.key = key;
      this.temporaryStudent = item;

      if (item.groupName) {
        if (item.assignedToOtherGroup) {
          this.dialogPopUp = true;
        } else {
          const studentId = this.temporaryStudent.studentId;
          const index = this.form.students.findIndex(student => student.studentId === studentId);
          if (index !== -1) this.form.students.splice(index, 1);
          this.students[this.key].assignedToOtherGroup = true;
          this.temporaryStudent = {};
        }
      }
    },
    popUpAgree() {
      this.students[this.key].assignedToOtherGroup = false;
      this.dialogPopUp = false;
      this.key = null;
    },
    popUpDisagree() {
      const studentId = this.temporaryStudent.studentId;
      const index = this.form.students.findIndex(student => student.studentId === studentId);
      if (index !== -1) this.form.students.splice(index, 1);
      this.students[this.key].assignedToOtherGroup = true;
      this.temporaryStudent = {};
      this.dialogPopUp = false;
      this.key = null;
    },
    getData() {
      this.$store.dispatch('GroupSchool/fetchStudentDetail', this.$route.params.id).then(res => {
        Object.keys(this.form).forEach(form => (this.form[form] = res[form]));
        this.hasLoad = true;
      });
    },
    getLevel() {
      if (this.$route.params.id) {
        this.table.loading = true;
        this.$store
          .dispatch('GroupSchool/fetchClassUpdate', this.$route.params.id)
          .then(res => (this.class_level = res))
          .catch(e => this.$store.dispatch('general/showErrorReqNotification', e))
          .finally((this.table.loading = false));
      } else {
        this.table.loading = true;
        this.$store
          .dispatch('GroupSchool/fetchClass')
          .then(res => (this.class_level = res))
          .catch(e => this.$store.dispatch('general/showErrorReqNotification', e))
          .finally((this.table.loading = false));
      }
    },
    getStudent() {
      const { id } = this.$route.params;
      this.table.loading = true;
      this.$store
        .dispatch(`GroupSchool/${id ? 'fetchStudentEdit' : 'fetchStudent'}`, this.$route.params.id)
        .then(students => {
          this.$set(this, 'students', students);
        })
        .finally(() => (this.table.loading = false));
    },
    submit() {
      if (this.form.students.length > 0) {
        this.$v.$touch();
        if (this.$v.$invalid) {
          return false;
        }

        this.loading = true;
        const payload = { ...(this.edit ? { id: this.$route.params.id } : null), ...this.form };

        this.$store
          .dispatch(`GroupSchool/${this.edit ? 'update' : 'save'}`, payload)
          .then(() => this.$router.push({ name: this.edit ? 'SchoolGroupSuccessUpdate' : 'SchoolGroupSuccess' }))
          .catch(e => this.$store.dispatch('general/showErrorReqNotification', e))
          .finally(() => (this.loading = false));
      } else {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_groupStudent_index') });
      }
    },
    backToList() {
      this.$router.push({ name: 'EnrollStudentIndex' });
    }
  },
  created() {
    this.getLevel();
    this.getStudent();
    if (this.$route.params.id) {
      this.getData();
      this.edit = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.move-list {
  &-title {
    background: #00BF63;
    color: #fff;
    font-family: Montserrat;
    font-weight: 800;
    border-radius: 8px 8px 0 0;
    padding: 10px;
    width: 100%;
  }

  &-content {
    border: 1px solid #d8d8d8;
    border-radius: 0 0 8px 8px;
    padding: 10px;
    overflow: scroll;
    overflow-x: hidden;
    width: 100%;
    height: 18em;
    line-height: 2em;

    ul {
      list-style: none;
      padding: 0;

      li {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #d8d8d8;
        padding: 10px 15px;
        align-items: center;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}

.move-listatas {
  &-titleatas {
    background: #00BF63;
    color: #fff;
    font-family: Montserrat;
    font-weight: 800;
    border-radius: 8px 8px 0 0;
    padding: 10px;
    width: 100%;
  }

  &-contentatas {
    border: 1px solid #d8d8d8;
    border-radius: 0 0 8px 8px;
    padding: 10px;
    overflow: scroll;
    overflow-x: hidden;
    width: 100%;
    height: 25em;
    line-height: 2em;

    ul {
      list-style: none;
      padding: 0;

      li {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #d8d8d8;
        padding: 10px 15px;
        align-items: center;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
.app-paginate {
  font-family: Montserrat;
  padding: 15px;

  &-info {
    font-weight: 500;
    font-size: 12px;
    color: #6d6d6d;
    text-align: center;
    margin-bottom: 15px;
  }

  @include query-min-sm() {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-info {
      margin-bottom: 0;
      text-align: left;
    }
  }
}
</style>
